import React, { useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import deleteIcon from "../../assets/icons/delete.svg";

import { useThunk } from "../../hooks/useThunk";
import {
  getAllCoupons,
  deleteCoupon,
} from "../../services/thunks/couponThunks";
import showToast from "../../utils/toastNotifications";

import Table from "../../components/Table/Table";
import CustomButton from "../../components/Buttons/CustomButton";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";

const CouponTable = ({
  refreshTrigger,
  highlightedRowId,
  setShowConfirm,
  setSelectedCoupon,
  showConfirm,
  selectedCoupon,
  setRefreshTrigger,
  statusFilter = null,
  isFixedAmount
}) => {
  const auth = useSelector((state) => state.auth);
  const [doDeleteCoupon, isDeletingCoupon] = useThunk(deleteCoupon);
  const tableRef = useRef(null);
  const pageSetterRef = useRef(null);
  const tableUpdateRowRef = useRef(null);
  const tableRemoveRowRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDelete = async (record) => {
    const result = await doDeleteCoupon({
      token: auth.token,
      code: record.code,
    });
    if (result.success) {
      showToast("success", "Coupon deleted successfully.");
      if (tableRemoveRowRef.current) {
        tableRemoveRowRef.current(record._id);
      }
    } else {
      showToast("error", result.error.message);
    }
  };

  const tableColumns = useMemo(() => {
    const baseColumns = [
      {
        title: "Coupon Code",
        dataIndex: "code",
        key: "code",
        render: (text) => (
          <div className="h-[32px] flex items-center text-sm font-[400] font-inter text-[#E2E2E2]">
            {text}
          </div>
        ),
      },
      {
        title: "Discount",
        dataIndex: isFixedAmount ? "discountAmount" : "discountType",
        key: isFixedAmount ? "discountAmount" : "discountType",
        render: (value, record) => {
          let displayValue = "";

          if (isFixedAmount) {
            // For fixed amount discounts, show the dollar amount
            displayValue = record.discountAmount
              ? `$${parseFloat(record.discountAmount).toFixed(2)}`
              : record.discountType || "N/A";
          } else {
            // For percentage discounts, show the discount type or amount with fallback
            displayValue = record.discountType ||
              (record.discountAmount ? `$${parseFloat(record.discountAmount).toFixed(2)}` : "N/A");
          }

          return (
            <div className="min-h-[32px] flex items-center text-sm font-[400] font-inter text-[#E2E2E2]">
              {displayValue}
            </div>
          );
        },
      },
      {
        title: "Plan",
        dataIndex: "planType",
        key: "planType",
        render: (text) => (
          <div className="max-h-[32px] flex items-center text-sm font-[400] font-inter text-[#E2E2E2]">
            {text}
          </div>
        ),
      },
    ];

    if (statusFilter === "Unlimited") {
      baseColumns.push(
        {
          title: "Influencer",
          dataIndex: "influencer",
          key: "influencer",
          render: (text) => (
            <div className="max-h-[32px] flex items-center text-sm font-[400] font-inter text-[#E2E2E2]">
              {text || "N/A"}
            </div>
          ),
        },
        {
          title: "Redemptions",
          dataIndex: "stripeTimeUsed",
          key: "stripeTimeUsed",
          render: (count) => (
            <div className="flex justify-center items-center bg-[#212121] rounded-[10px] w-[50px] h-[25px] leading-[24px] text-[#E2E2E2] text-sm text-center font-[400] font-inter">
              {count || 0}
            </div>
          ),
        }
      );
    } else {
      baseColumns.push({
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (text = "Unused") => (
          <div
            className={`flex items-center text-sm font-[400] font-inter ${text === "Used" ? "text-[#E57819]" : "text-[#07C01D]"
              }`}
          >
            {text}
          </div>
        ),
      });
    }

    baseColumns.push(
      {
        title: "Created On",
        dataIndex: "createdOn",
        key: "createdOn",
        render: (text) => {
          const formatted = format(new Date(text), "MMM dd, yyyy - HH.mm");
          return (
            <div className="max-h-[28px] text-sm font-[400] font-inter text-[#E2E2E2]">
              {formatted}
            </div>
          );
        },
      },
      {
        title: "Action",
        key: "action",
        render: (_, record) => (
          <CustomButton
            onClick={() => {
              setSelectedCoupon(record);
              setShowConfirm(true);
            }}
            className="flex items-center justify-center gap-2 px-4 py-1 border border-[#C00707] text-[#C00707] rounded-[15px] text-xs font-normal w-[90px] hover:bg-[#330000] transition font-inter"
          >
            <img src={deleteIcon} alt="delete" className="w-3 h-3" />
            Delete
          </CustomButton>
        ),
      }
    );

    return baseColumns;
  }, [statusFilter]);

  return (
    <div ref={tableRef}>
      <Table
        columns={tableColumns}
        fetchData={getAllCoupons}
        refreshTrigger={refreshTrigger}
        rowKey="_id"
        dataKey="items"
        headingBgColor="#0D141B"
        headingTextColor="#FFFFFF"
        initialPageSize={25}
        highlightedRowId={highlightedRowId}
        setPageCallback={(fn) => (pageSetterRef.current = fn)}
        customQueryParams={(params) => ({
          ...params,
          token: auth.token,
          statusFilter: statusFilter || undefined,
          isFixedAmount: isFixedAmount, 
        })}
        updateRowRef={tableUpdateRowRef}
        removeRowRef={tableRemoveRowRef}
      />

      <ConfirmDeleteModal
        visible={showConfirm}
        onConfirm={async () => {
          await handleDelete(selectedCoupon);
          setShowConfirm(false);
          setSelectedCoupon(null);
        }}
        onCancel={() => {
          setShowConfirm(false);
          setSelectedCoupon(null);
        }}
        title="Confirm Deletion"
        content={`Are you sure you want to delete the coupon "${selectedCoupon?.code}"?`}
        disabled={isDeletingCoupon}
        loading={isDeletingCoupon}
      />
    </div>
  );
};

export default CouponTable;
